import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useMlsProviderStore } from '../../store/providers/MlsProviders';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './MlsProviderListSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function MlsProviders() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useMlsProviderStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_MLSPROVIDERS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (mlsProvider) => {
        navigate(`/mlsProviders/edit/${mlsProvider.id}`);
    };

    const addedHandler = () => {
        navigate(`/mlsProviders/new`);
    };

    const deletedHandler = (mlsProvider) => {
        deleteRecord({ mlsProviderId: mlsProvider.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'mls-providers', parent: 'Settings', text: 'MLS Providers', path: '/mlsProviders' }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="MLS Providers" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="MLS Provider"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        permissionEdit={canEdit}
                                        activeValue="Active"
                                        activeField="statusName"
                                        added={() => addedHandler()}
                                        selected={(mlsProvider) => selectedHandler(mlsProvider)}
                                        deleted={(mlsProvider) => deletedHandler(mlsProvider)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(MlsProviders));